import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Send, Mail, Phone } from "lucide-react";
import { createLead, isSupabaseConfigured } from "../lib/supabaseApi";
import { BUSINESS } from "../lib/business";

const PROJECT_TYPES = [
  "Web App",
  "Mobile App",
  "AI Product",
  "SaaS Platform",
  "Full Product",
  "Consulting",
];

export default function CTA() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    project_type: "",
    message: "",
    website: "", // honeypot — must stay empty
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill name, email, and project details.");
      return;
    }
    if (!isSupabaseConfigured) {
      toast.error("Backend not configured. Set Supabase environment variables.");
      return;
    }
    setSubmitting(true);
    try {
      await createLead({
        name: form.name.trim(),
        email: form.email.trim(),
        project_type: form.project_type || null,
        message: form.message.trim(),
        website: form.website, // honeypot
      });
      toast.success("Request received. We'll be in touch within 24h.");
      setForm({ name: "", email: "", project_type: "", message: "", website: "" });
    } catch (err) {
      const msg = err?.message || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="cta"
      className="relative py-24 md:py-32"
      data-testid="cta-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="relative rounded-3xl overflow-hidden border border-white/10 bg-ink-50">
          {/* Glow background */}
          <div
            aria-hidden
            className="absolute inset-0 opacity-80 pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse 60% 50% at 20% 20%, rgba(0,229,255,0.15), transparent 60%), radial-gradient(ellipse 60% 60% at 80% 90%, rgba(108,99,255,0.18), transparent 60%)",
            }}
          />
          <div className="absolute inset-0 tech-grid-fine opacity-30 pointer-events-none" />

          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 p-5 sm:p-8 md:p-12 lg:p-16 xl:p-20">
            {/* Left */}
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-4">
                / 08 — Start your project
              </div>
              <h2
                className="font-display text-2xl sm:text-3xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight"
                data-testid="cta-heading"
              >
                From idea to
                <br />
                complete{" "}
                <span className="text-accent-cyan italic font-light">
                  product.
                </span>
              </h2>
              <p className="mt-6 text-zinc-400 max-w-md leading-relaxed">
                Web app, AI system, SaaS platform, hardware device, or
                3D-printed product — whatever you're building, we've covered it.
                Tell us what you need. Honest scoping, clear pricing,
                and integrated execution across every discipline.
              </p>

              <div className="mt-8 space-y-3">
                <a
                  href={`mailto:${BUSINESS.email}`}
                  data-testid="cta-email-link"
                  className="inline-flex items-center gap-2 text-zinc-300 hover:text-white transition-colors"
                >
                  <Mail size={16} strokeWidth={2} />
                  <span className="font-mono text-xs sm:text-sm tracking-tight break-all">
                    {BUSINESS.email}
                  </span>
                </a>
                <a
                  href={BUSINESS.phoneHref}
                  data-testid="cta-phone-link"
                  className="flex items-center gap-2 text-zinc-300 hover:text-white transition-colors"
                >
                  <Phone size={16} strokeWidth={2} />
                  <span className="font-mono text-xs sm:text-sm tracking-tight">
                    {BUSINESS.phoneDisplay}
                  </span>
                </a>
                <p className="text-xs text-zinc-500 leading-relaxed max-w-sm">
                  {BUSINESS.legalName} · {BUSINESS.addressOneLine}
                </p>
              </div>

              <div className="mt-10 flex items-center gap-4 text-xs text-zinc-500">
                <div className="flex items-center gap-2">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent-cyan opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-accent-cyan" />
                  </span>
                  <span className="font-mono uppercase tracking-[0.2em]">
                    2 slots open · Q2 2026
                  </span>
                </div>
              </div>
            </div>

            {/* Form */}
            <form
              onSubmit={onSubmit}
              data-testid="cta-form"
              className="bg-ink-0/60 backdrop-blur-xl border border-white/10 rounded-2xl p-4 sm:p-6 md:p-8 space-y-5"
            >
              {/* Honeypot — visually hidden, not tab-reachable */}
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: "-9999px",
                  width: "1px",
                  height: "1px",
                  overflow: "hidden",
                }}
              >
                <label>
                  Website
                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={(e) => onChange("website", e.target.value)}
                  />
                </label>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                    Name
                  </span>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => onChange("name", e.target.value)}
                    placeholder="Jane Doe"
                    data-testid="cta-input-name"
                    className="mt-2 w-full bg-transparent border-b border-white/10 text-white py-2 placeholder-zinc-600 outline-none focus:border-accent-cyan transition-colors"
                  />
                </label>
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                    Email
                  </span>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => onChange("email", e.target.value)}
                    placeholder="jane@company.com"
                    data-testid="cta-input-email"
                    className="mt-2 w-full bg-transparent border-b border-white/10 text-white py-2 placeholder-zinc-600 outline-none focus:border-accent-cyan transition-colors"
                  />
                </label>
              </div>

              <div>
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                  Project type
                </span>
                <div className="mt-2 flex flex-wrap gap-2">
                  {PROJECT_TYPES.map((t) => {
                    const active = form.project_type === t;
                    return (
                      <button
                        type="button"
                        key={t}
                        onClick={() => onChange("project_type", active ? "" : t)}
                        data-testid={`cta-type-${t.toLowerCase().replace(/[\s/]+/g, "-")}`}
                        className={`font-mono text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full border transition-all ${
                          active
                            ? "border-accent-cyan/60 bg-accent-cyan/10 text-accent-cyan"
                            : "border-white/10 text-zinc-400 hover:border-white/30 hover:text-white"
                        }`}
                      >
                        {t}
                      </button>
                    );
                  })}
                </div>
              </div>

              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                  Tell us about it
                </span>
                <textarea
                  required
                  rows={4}
                  value={form.message}
                  onChange={(e) => onChange("message", e.target.value)}
                  placeholder="Describe what you're building — problem, timeline, and budget if known."
                  data-testid="cta-input-message"
                  className="mt-2 w-full bg-transparent border-b border-white/10 text-white py-2 placeholder-zinc-600 outline-none focus:border-accent-cyan transition-colors resize-none"
                />
              </label>

              <button
                type="submit"
                disabled={submitting}
                data-testid="cta-submit"
                className="group w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-white text-black text-sm font-semibold hover:bg-zinc-200 transition-all hover:scale-[1.01] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  "Sending…"
                ) : (
                  <>
                    Start your project
                    <ArrowRight
                      size={16}
                      strokeWidth={2.5}
                      className="group-hover:translate-x-1 transition-transform"
                    />
                  </>
                )}
              </button>

              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600 text-center pt-2">
                <Send size={10} className="inline -mt-0.5 mr-1" />
                Replies within 24 hours · No spam, ever
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
